import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { bookingsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import '../styles/MyBookings.css';

const MyBookings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [isAuthenticated, navigate]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getMyBookings();
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm(t('booking.cancelConfirm'))) return;
    try {
      await bookingsAPI.cancel(bookingId);
      toast.success(t('booking.cancelSuccess'));
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.message || t('booking.cancelError'));
    }
  };

  const currentBookings = bookings.filter((booking) =>
    ['pending', 'confirmed'].includes(booking.status)
  );
  const pastBookings = bookings.filter((booking) =>
    ['completed', 'cancelled'].includes(booking.status)
  );

  const renderBookingCards = (bookingList) => (
    <div className="bookings-list">
      {bookingList.map((booking) => (
        <div key={booking._id} className="booking-item">
          <div className="booking-header">
            <h3>
              {i18n.resolvedLanguage?.startsWith('fr')
                ? booking.serviceId?.name?.fr
                : booking.serviceId?.name?.en}
            </h3>
            <span className={`status ${booking.status}`}>
              {t(`admin.${booking.status}`)}
            </span>
          </div>

          <div className="booking-details">
            <p><strong>{t('booking.date')}:</strong>{' '}{booking.status === 'confirmed'
              ? new Date(booking.dateTime).toLocaleString(i18n.resolvedLanguage)
              : new Date(booking.dateTime).toLocaleDateString(i18n.resolvedLanguage)}</p>
            <p><strong>{t('booking.price')}:</strong>{' '}
              {booking.discountPercentage > 0 && <del>${booking.originalPrice}</del>} ${booking.totalPrice}
            </p>
            {booking.discountPercentage > 0 && (
              <p className="booking-discount-line">
                <strong>{t('booking.discountApplied', { percentage: booking.discountPercentage })}:</strong>{' '}
                {booking.discountReason?.[i18n.resolvedLanguage?.startsWith('fr') ? 'fr' : 'en']}
              </p>
            )}
            {booking.serviceConfiguration?.length > 0 && (
              <div className="booking-configuration-summary">
                {booking.serviceConfiguration.filter((item) => Number(item.quantity) > 0).map((item) => (
                  <p key={item.optionId}>
                    <strong>{(i18n.resolvedLanguage?.startsWith('fr') ? item.name?.fr : item.name?.en) || item.name?.en}:</strong> {item.quantity}
                  </p>
                ))}
              </div>
            )}
            {booking.contactPhone && <p><strong>{t('profile.phone')}:</strong> {booking.contactPhone}</p>}
            {booking.cleaningAddress && (
              <p><strong>{t('booking.cleaningAddress')}:</strong>{' '}{[
                booking.cleaningAddress.street,
                booking.cleaningAddress.unit,
                booking.cleaningAddress.city,
                booking.cleaningAddress.province,
                booking.cleaningAddress.postalCode
              ].filter(Boolean).join(', ')}</p>
            )}
            {booking.clientNotes && <p><strong>{t('booking.notes')}:</strong> {booking.clientNotes}</p>}
          </div>
          {['pending', 'confirmed'].includes(booking.status) && (
            <div className="booking-actions">
              <button type="button" className="edit-booking-btn" onClick={() => navigate(`/booking?edit=${booking._id}`)}>
                {t('booking.edit')}
              </button>
              <button type="button" className="cancel-booking-btn" onClick={() => handleCancel(booking._id)}>
                {t('booking.cancelBooking')}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="my-bookings-page">
      <h1>{t('booking.myBookings')}</h1>

      {bookings.length === 0 ? (
        <div className="no-bookings">
          <p>{t('booking.noBookings')} <a href="/services">{t('booking.bookAService')}</a></p>
        </div>
      ) : (
        <div className="booking-sections">
          <section className="booking-section">
            <h2>{t('booking.currentBookings')}</h2>
            {currentBookings.length > 0
              ? renderBookingCards(currentBookings)
              : <p className="empty-booking-section">{t('booking.noCurrentBookings')}</p>}
          </section>
          <section className="booking-section booking-history-section">
            <h2>{t('booking.bookingHistory')}</h2>
            {pastBookings.length > 0
              ? renderBookingCards(pastBookings)
              : <p className="empty-booking-section">{t('booking.noBookingHistory')}</p>}
          </section>
        </div>
      )}
    </div>
  );
};

export default MyBookings;
